import { animations } from './animations'

const INVIEW_DISABLED_MQ = '(max-width: 1279px)'

const isInviewScrollDisabled = (): boolean =>
	typeof window !== 'undefined' && window.matchMedia(INVIEW_DISABLED_MQ).matches

const defaults = {
	scrub: false,
	once: true,
	offset: 0,
	delay: 0,
	stagger: 0.12,
}

/**
 * Primer nodo con `data-inview` en el bloque: el propio trigger si lleva ambos
 * atributos; si no, el primer descendiente que coincida.
 */
const firstInviewDescendant = (root: Element): HTMLElement | null => {
	const h = root as HTMLElement
	if (h.matches?.('[data-inview]')) return h
	return root.querySelector('[data-inview]') as HTMLElement | null
}

/**
 * Opciones de ScrollTrigger (repeat, scrub, offset, stagger entre hijos): se leen del trigger;
 * si no están ahí, del primer `[data-inview]` interior (p. ej. hero).
 * `data-inview-delay` en hijos va al tween en `setInviewItem`; el delay de
 * ScrollTrigger solo se toma del elemento con `data-inview-trigger`.
 */
const triggerConfigSources = (root: Element) => {
	const h = root as HTMLElement
	const inner = firstInviewDescendant(root)
	return { root: h, inner }
}

const setInviewItem = (el: Element) => {
	const key = (el as HTMLElement).dataset.inview || ''
	const fn = (animations as any)[key]
	if (!fn) return null
	const anim = fn(el)
	
	const rawDelay = (el as HTMLElement).dataset.inviewDelay
	if (rawDelay !== undefined && rawDelay !== '') {
		const d = parseFloat(rawDelay)
		if (!Number.isNaN(d)) anim.settings.delay = d
	}
	return anim
}

class InviewTrigger {
	el: Element
	offset: number
	childStagger: number = defaults.stagger
	settings: any
	items: any[]
	anim: any

	constructor(el: Element) {
		this.el = el
		const { root, inner } = triggerConfigSources(el)
		const offsetRaw = root.dataset.inviewOffset || inner?.dataset.inviewOffset
		this.offset =
			offsetRaw !== undefined && offsetRaw !== ''
				? parseFloat(offsetRaw as string) + defaults.offset
				: defaults.offset
		this.settings = {}
		this.items = []
		this.anim = gsap.timeline()
	}

	init() {
		const { root, inner } = triggerConfigSources(this.el)
		const repeat =
			root.hasAttribute('data-inview-repeat') || Boolean(inner?.hasAttribute('data-inview-repeat'))
		this.settings.once = repeat ? false : defaults.once
		const scrubAttr = root.dataset.inviewScrub || inner?.dataset.inviewScrub
		this.settings.scrub = scrubAttr ? true : defaults.scrub
		this.settings.start = 'top bottom-=' + this.offset
		this.settings.end = '+=120'
		// Solo en el trigger: evita sumar dos veces si el hijo ya usa `data-inview-delay` en el tween.
		this.settings.delay = root.dataset.inviewDelay
			? parseFloat(root.dataset.inviewDelay as string)
			: defaults.delay
		this.settings.toggleActions = this.settings.once ? 'play none none none' : 'restart none none reverse'

		const staggerRaw = root.dataset.inviewStagger || inner?.dataset.inviewStagger
		this.childStagger =
			staggerRaw !== undefined && staggerRaw !== ''
				? parseFloat(staggerRaw as string)
				: defaults.stagger

		const rootEl = this.el as HTMLElement
		// `querySelectorAll` no incluye el propio elemento: si lleva `data-inview`, animamos también el trigger.
		if (rootEl.matches?.('[data-inview]')) {
			const v = setInviewItem(rootEl)
			if (v) this.items.push(v)
		}
		this.el.querySelectorAll('[data-inview]').forEach((item) => {
			const v = setInviewItem(item as Element)
			if (v) this.items.push(v)
		})
		
		this.items.forEach((item: any) => {
			item.setFrom()
			this.anim.add(item.animation, '<+' + this.childStagger)
		})

		this.anim.pause()
	}

	initTrigger() {
		ScrollTrigger.create({
			trigger: this.el,
			...this.settings,
			animation: this.anim,
			toggleClass: 'is-inview'
		})
	}
}

class Inview {
	triggers: any[] = []

	set(triggers: Element[]) {
		this.triggers = []

		triggers.forEach((el) => {
			const trigger = new InviewTrigger(el)
			trigger.init()
			this.triggers.push(trigger)
		})
	}

	prepare(nextContainer?: Element) {
		document.documentElement.classList.remove('hg-inview-primed')

		const container = nextContainer || document

		if (isInviewScrollDisabled()) {
			this.triggers = []
			container.querySelectorAll('[data-inview-trigger]').forEach((el) => {
				el.classList.add('is-inview')
			})
			document.documentElement.classList.add('hg-inview-primed')
			return
		}

		const triggers = container.querySelectorAll('[data-inview-trigger]')

		this.set(Array.from(triggers) as any)

		document.documentElement.classList.add('hg-inview-primed')
	}

	commitTriggers() {
		if (isInviewScrollDisabled()) return
		this.triggers.forEach((trigger: any) => trigger.initTrigger())
	}

	init(nextContainer?: Element) {
		this.prepare(nextContainer)
		this.commitTriggers()
	}

	update(nextContainer?: Element) {
		this.init(nextContainer)
	}
}

export default new Inview()

